import { Children, cloneElement, forwardRef, isValidElement } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const overviewItemVariants = cva(
  'flex flex-col gap-x-3.5 gap-y-1 border-b border-grey-mid px-5 py-4 last:border-none sm:flex-row',
  {
    variants: {
      align: {
        start: 'sm:items-start',
        center: 'sm:items-center',
        end: 'sm:items-end',
      },
      cols: {
        1: 'col-span-2 sm:col-span-1',
        2: 'col-span-2',
      },
      size: {
        sm: 'text-sm',
        lg: 'text-base',
      },
    },
  },
);

export interface OverviewProps extends React.HTMLAttributes<HTMLUListElement> {
  cols?: 1 | 2;
  labelClassName?: string;
}

export interface OverviewItemProps extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'value'> {
  label?: React.ReactNode;
  value?: React.ReactNode;
  cols?: 1 | 2;
  size?: 'sm' | 'lg';
  align?: 'start' | 'center' | 'end';
  labelClassName?: string;
}

export const Overview = forwardRef<HTMLUListElement, OverviewProps>(
  ({ cols, labelClassName, hidden, className, children, ...restProps }, ref) => {
    if (hidden || !children) {
      return null;
    }

    return (
      <ul
        ref={ref}
        className={twMerge(
          'grid grid-cols-2 items-center rounded-md border border-grey-mid bg-grey-light',
          className,
        )}
        {...restProps}
      >
        {Children.map(children, (child) =>
          isValidElement<{ cols?: 1 | 2; labelClassName?: string }>(child)
            ? cloneElement(child, {
                ...child.props,
                cols,
                labelClassName,
              })
            : null,
        )}
      </ul>
    );
  },
);

export const OverviewItem = forwardRef<HTMLLIElement, OverviewItemProps>(
  (
    {
      label,
      value,
      cols = 2,
      size = 'lg',
      align = 'center',
      hidden,
      children,
      className,
      labelClassName,
      ...restProps
    },
    ref,
  ) => {
    if (hidden) {
      return null;
    }

    // Callers may pass the value via the `value` prop or as `children`.
    // Children win when both are present so existing wrappers
    // (e.g. SettingsOverview) keep working.
    const body = children ?? value;

    return (
      <li
        ref={ref}
        className={twMerge(
          overviewItemVariants({
            cols,
            size,
            align,
            className,
          }),
        )}
        {...restProps}
      >
        {label ? (
          <>
            <div className={twMerge('min-w-[130px] font-medium', labelClassName)}>{label}</div>
            <div className="flex-1 font-normal">{body}</div>
          </>
        ) : (
          body
        )}
      </li>
    );
  },
);

Overview.displayName = 'Overview';
OverviewItem.displayName = 'OverviewItem';
